/**
 * 格式化停靠点编号显示
 * 根据业务需求：如果 subStopNumber 不为 null 而且不等于 "0"，显示格式为 ${stopNumber}-${subStopNumber}，否则显示为 ${stopNumber}
 * 
 * @param stopNumber 停靠点编号
 * @param subStopNumber 子停靠点编号（字符串类型）
 * @returns 格式化后的停靠点编号字符串
 */
export const formatStopNumber = (stopNumber: number, subStopNumber?: string | null): string => {
  if (subStopNumber != null && subStopNumber !== "0" && subStopNumber !== "") {
    return `${stopNumber}-${subStopNumber}`;
  }
  return `${stopNumber}`;
};

/**
 * 从 DeliveryTaskOrderType 对象中格式化停靠点编号
 * 
 * @param deliveryTaskOrder 配送任务订单对象
 * @returns 格式化后的停靠点编号字符串
 */
export const formatStopNumberFromDeliveryTaskOrder = (deliveryTaskOrder: { stopNumber: number; subStopNumber?: string | null }): string => {
  return formatStopNumber(deliveryTaskOrder.stopNumber, deliveryTaskOrder.subStopNumber);
};

// 集群订单排序函数
export const sortClusterOrders = (orders: any[]) => {
    return [...orders].sort((a, b) => {
        // 获取格式化后的停靠点编号
        const aStop = formatStopNumberFromDeliveryTaskOrder(a);
        const bStop = formatStopNumberFromDeliveryTaskOrder(b);

        // 解析主编号和后缀
        const parseStopNumber = (stop: string) => {
            const parts = stop.split('-');
            return {
                main: parseInt(parts[0]),
                sub: parts[1] || '', // 如果没有后缀，返回空字符串
                hasSub: parts.length > 1
            };
        };

        const aParts = parseStopNumber(aStop);
        const bParts = parseStopNumber(bStop);

        // 首先按后缀类型排序：数字-英文 > 数字-数字 > 数字（无横杠）
        const aHasSub = aParts.hasSub;
        const bHasSub = bParts.hasSub;
        
        // 获取后缀类型优先级：0=无后缀, 1=数字后缀, 2=字母后缀
        const getSuffixPriority = (hasSub: boolean, sub: string) => {
            if (!hasSub) return 0; // 无后缀
            if (/[a-zA-Z]/.test(sub)) return 2; // 字母后缀
            return 1; // 数字后缀
        };
        
        const aPriority = getSuffixPriority(aHasSub, aParts.sub);
        const bPriority = getSuffixPriority(bHasSub, bParts.sub);
        
        // 按优先级排序
        if (aPriority !== bPriority) {
            return bPriority - aPriority; // 优先级高的排在前面
        }
        
        // 优先级相同，按主编号排序
        if (aParts.main !== bParts.main) {
            return aParts.main - bParts.main;
        }
        
        // 主编号也相同，按后缀内容排序
        const aSub = aParts.sub;
        const bSub = bParts.sub;
        
        // 都是字母后缀，按长度优先，然后按字母顺序
        if (aPriority === 2) {
            if (aSub.length !== bSub.length) {
                return aSub.length - bSub.length;
            }
            return aSub.localeCompare(bSub);
        }
        
        // 都是数字后缀，按数字大小排序
        if (aPriority === 1) {
            const aSubNum = parseInt(aSub);
            const bSubNum = parseInt(bSub);
            return aSubNum - bSubNum;
        }
        
        // 都是无后缀，已经按主编号排序了
        return 0;
    });
};

/**
 * 配送任务订单排序比较函数
 * 排序规则：
 * 1. stopNumber 为 0 的排在最前面
 * 2. 其他按照 stopNumber 排序
 * 3. 如果 stopNumber 相同，按照 subStopNumber 排序（小的在前）
 * 
 * @param a 第一个订单
 * @param b 第二个订单
 * @returns 比较结果
 */
export const compareDeliveryTaskOrders = (
  a: { routeName?: string; stopNumber: number; subStopNumber?: string | null; deliveryTaskId: number},
  b: { routeName?: string; stopNumber: number; subStopNumber?: string | null; deliveryTaskId: number}
): number => {

  // 先按routeName排序
  const routeCompare = (a.routeName || '').localeCompare(b.routeName || '');
  if (routeCompare !== 0) {
    return routeCompare;
  }

  // stopNumber 为 0 的排在最前面
  if (a.stopNumber === 0 && b.stopNumber !== 0) return -1;
  if (a.stopNumber !== 0 && b.stopNumber === 0) return 1;
  
  // 其他按照stopNumber排序
  const stopCompare = a.stopNumber - b.stopNumber;
  if (stopCompare !== 0) {
    return stopCompare;
  }

  // 如果stopNumber相同，deliveryTaskId小的排前边
  const deliveryTaskIdCompare = a.deliveryTaskId - b.deliveryTaskId
  if (deliveryTaskIdCompare !== 0) {
    return deliveryTaskIdCompare;
  }

  // 如果stopNumber、deliveryTaskId相同，按照subStopNumber排序（小的在前）
  // 将字符串转换为数字进行比较，如果无法转换则使用字符串比较
  const aSubStop = parseFloat(a.subStopNumber || "0") || 0;
  const bSubStop = parseFloat(b.subStopNumber || "0") || 0;
  
  // 如果都是有效数字，按数字比较
  if (!isNaN(aSubStop) && !isNaN(bSubStop)) {
    return aSubStop - bSubStop;
  }
  
  // 否则按字符串比较
  return (a.subStopNumber || "0").localeCompare(b.subStopNumber || "0");
};

/**
 * 将子停靠点编号转换为排序值
 * 用于处理混合数字和字母的排序，如：1-a, 1-aa, 1-ba, 1-aaa
 * 
 * @param str 子停靠点编号字符串
 * @returns 排序用的数值
 */
export const convertToSortValue = (str: string): number => {
  if (str === '0') return 0;
  let value = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str[i];
    const charValue = char.charCodeAt(0) - 'a'.charCodeAt(0) + 1;
    value = value * 26 + charValue;
  }
  return value;
};

/**
 * 比较子停靠点编号
 * 排序规则：字母（重复地址）排在数字（插单）前面
 * 
 * @param subA 第一个子停靠点编号
 * @param subB 第二个子停靠点编号
 * @returns 比较结果
 */
export const compareSubStopNumber = (subA: string | null | undefined, subB: string | null | undefined): number => {
  const strA = String(subA || '0');
  const strB = String(subB || '0');
  
  const isNumA = /^\d+$/.test(strA);
  const isNumB = /^\d+$/.test(strB);
  
  // 字母排在数字前面（重复地址在插单前）
  if (!isNumA && isNumB) return -1;
  if (isNumA && !isNumB) return 1;

  // 都是数字，按数值大小排序
  if (isNumA && isNumB) {
    return parseInt(strA) - parseInt(strB);
  }

  // 都是字母，按自定义规则排序
  return convertToSortValue(strA) - convertToSortValue(strB);
};

/**
 * 判断 routeName 是否是分段路由
 * 分段路由格式：B008-1, B008-2 等（包含 -数字）
 * 不分段路由格式：B008（不包含 -数字）
 * 
 * @param routeName 路由名称
 * @returns 是否是分段路由
 */
const isSegmentedRoute = (routeName: string | null | undefined): boolean => {
  if (!routeName) return false;
  // 检查是否包含 -数字 格式
  return /-\d+$/.test(routeName);
};

/**
 * 配送任务订单排序函数（包含路线历史ID、分段路由、停靠点编号的完整排序）
 * 
 * @param a 第一个订单
 * @param b 第二个订单
 * @param checkSameAreaEnabled 是否启用同区域校验，只有为 true 时才应用第二级和第三级排序
 * @returns 比较结果
 */
export const sortDeliveryOrders = (
  a: {
    deliveryTaskId: number;
    routeHistoryId: number;
    routeName?: string | null;
    stopNumber: number; 
    subStopNumber?: string | null; 
  },
  b: { 
    deliveryTaskId: number;
    routeHistoryId: number;
    routeName?: string | null;
    stopNumber: number; 
    subStopNumber?: string | null; 
  },
  checkSameAreaEnabled?: boolean
): number => {
  // 第一级：按 routeHistoryId 排序，小的在前面
  if (a.routeHistoryId !== b.routeHistoryId) {
    return a.routeHistoryId - b.routeHistoryId;
  }

  // 第二级和第三级：只有当 checkSameAreaEnabled 为 true 时才生效
  if (checkSameAreaEnabled === true) {
    // 第二级：同一个 routeHistoryId 下，分段路由在不分段路由前面
    const aIsSegmented = isSegmentedRoute(a.routeName);
    const bIsSegmented = isSegmentedRoute(b.routeName);
    
    if (aIsSegmented !== bIsSegmented) {
      // 分段路由排在前面
      return aIsSegmented ? -1 : 1;
    }

    // 第三级：如果都是分段路由，按 deliveryTaskId 排序（小的在前面）
    if (aIsSegmented && bIsSegmented) {
      if (a.deliveryTaskId !== b.deliveryTaskId) {
        return a.deliveryTaskId - b.deliveryTaskId;
      }
    }
  }

  // 第四级：按 stopNumber 排序（升序）
  if (a.stopNumber !== b.stopNumber) {
    return a.stopNumber - b.stopNumber;
  }

  // 第五级：同一 stopNumber 内按 subStopNumber 排序
  return compareSubStopNumber(a.subStopNumber, b.subStopNumber);
};
